import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import { closeModal } from 'modal/store/actions';
import FormikInputField from 'base/components/forms/formik/FormikInputField';
import FormikSelectField from 'base/components/forms/formik/FormikSelectField';
import SubmitButton from 'base/components/forms/SubmitButton';
import FieldWrapper from 'base/components/forms/FieldWrapper';
import MainTitle from 'base/components/MainTitle';
import Fieldset from 'base/components/forms/Fieldset';
import Legend from 'base/components/forms/Legend';
import Title3 from 'base/components/Title3';
import FormikTextareaField from 'base/components/forms/formik/FormikTextareaField';
import ModalHeader from 'modal/components/ModalHeader';
import { shareDocuments } from '../../store/documents/actions';
import { getSelectedDocuments, getSelectedFolders } from '../../store/fileexplorer/selectors';
import api from '../../../api';


function DocumentShareModal({
  name,
  closeModal,
  selectedDocuments,
  selectedFolders,
  shareDocuments,
}) {
  const documents = selectedDocuments;
  const folders = selectedFolders;
  const [roleSelectOptions, setRoleSelectOptions] = useState([]);
  const [userSelectOptions, setUserSelectOptions] = useState([]);
  const [mailingListSelectOptions, setMailingListSelectOptions] = useState([]);

  const [documentIds, setDocumentIds] = useState([]);
  const [folderIds, setFolderIds] = useState([]);

  useEffect(() => {
    setDocumentIds(documents.map((d) => d.id));
    setFolderIds(folders.map((f) => f.id));
  }, [documents, folders]);

  useEffect(() => {
    api.documents.getAuthorizedRoles(documentIds, folderIds).toPromise()
      .then((result) => {
        if (result.body.length > 0) {
          const options = result.body.map((role) => ({ value: role.id, label: role.name }));
          setRoleSelectOptions(options);
        }
      })
      .catch(e => console.error(e)); // eslint-disable-line

    api.documents.getAuthorizedUsers(documentIds, folderIds).toPromise()
      .then((result) => {
        if (result.body.length > 0) {
          const options = result.body.map((user) => ({
            value: user.id,
            label: `${user.firstName} ${user.lastName}`,
          }));
          setUserSelectOptions(options);
        }
      })
        .catch(e => console.error(e)); // eslint-disable-line

    api.documents.getAuthorizedMailingLists(documentIds, folderIds).toPromise()
      .then((result) => {
        if (result.body.length > 0) {
          const options = result.body.map((mailingList) => ({
            value: mailingList.id,
            label: mailingList.name,
          }));
          setMailingListSelectOptions(options);
        }
      })
        .catch(e => console.error(e)); // eslint-disable-line
  }, [documentIds, folderIds]);

  return (
    <>
      <ModalHeader>
        <MainTitle>Share documents</MainTitle>
      </ModalHeader>
      <Formik
        initialValues={{
          roles: [],
          users: [],
          ccUsers: [],
          subject: '',
          message: '',
        }}
        validationSchema={
              Yup.object().shape({
                subject: Yup.string()
                  .required('Subject is required.'),
                message: Yup.string()
                  .required('Text is required.'),
              })
            }
            render={({errors, isSubmitting}) => ( // eslint-disable-line
              <Form>
                <Fieldset>
                  <Legend>Recipients</Legend>
                  <Field
                    type="select"
                    name="users"
                    component={FormikSelectField}
                    placeholder="Individual users"
                    label="Individual Users"
                    isMulti
                    options={userSelectOptions}
                  />
                  <Field
                    type="select"
                    name="roles"
                    component={FormikSelectField}
                    placeholder="All users with role"
                    label="All users with role"
                    isMulti
                    options={roleSelectOptions}
                  />
                  <Field
                    type="select"
                    name="mailingLists"
                    component={FormikSelectField}
                    placeholder="All users within mailing list"
                    label="All users within mailing list"
                    isMulti
                    options={mailingListSelectOptions}
                  />
                  <Field
                    type="select"
                    name="ccUsers"
                    component={FormikSelectField}
                    placeholder="CC users"
                    label="CC Users"
                    isMulti
                    options={userSelectOptions}
                  />
                </Fieldset>

                <Fieldset>
                  <Legend>Message</Legend>
                  <Field
                    type="text"
                    name="subject"
                    label="Subject"
                    placeholder="Subject"
                    component={FormikInputField}
                  />
                  <Field
                    type="text"
                    name="message"
                    label="Text"
                    placeholder="Text"
                    rows="6"
                    component={FormikTextareaField}
                  />
                </Fieldset>

                <SubmitButton type="submit" disabled={isSubmitting}>
                    Share now
                </SubmitButton>

                {documents && documents.length > 0 && (
                  <FieldWrapper style={{ margin: '4rem 0 4rem 0' }}>
                    <Title3>Documents to share</Title3>
                    {documents.map((document) => (<div key={document.id}>{document.name}</div>))}
                  </FieldWrapper>
                )}

                {folders && folders.length > 0 && (
                  <FieldWrapper style={{ margin: '4rem 0 4rem 0' }}>
                    <Title3>Folders to share</Title3>
                    {folders.map((folder) => (<div key={folder.id}>{folder.name}</div>))}
                  </FieldWrapper>
                )}

              </Form>
            )}
            onSubmit={(values) => { // eslint-disable-line
              shareDocuments(
                documents && documents.length > 0 ? documents.map((d) => d.id) : [],
                folders && folders.length > 0 ? folders.map((f) => f.id) : [],
                values.users,
                values.roles,
                values.mailingLists,
                values.ccUsers,
                values.subject,
                values.message,
              );
              closeModal(name);
            }}
      />
    </>
  );
}

DocumentShareModal.propTypes = {
  name: PropTypes.string.isRequired,
  closeModal: PropTypes.func.isRequired,
  selectedDocuments: PropTypes.arrayOf(PropTypes.any).isRequired,
  selectedFolders: PropTypes.arrayOf(PropTypes.any).isRequired,
  shareDocuments: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  selectedDocuments: getSelectedDocuments(state),
  selectedFolders: getSelectedFolders(state),
});

const mapDispatchToProps = {
  closeModal,
  shareDocuments,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(DocumentShareModal);
